/**
 * The compact "Serving Guide" / "Tasting Profile" links under Add to cart.
 *
 * The source (caviar-tasting-profile-modal.tsx, caviar-serving-guide.tsx) opens
 * these in a modal. Rather than duplicate that content into the buy box — which
 * would give the guide two sources of truth and two places for staff to edit —
 * each trigger CLONES the matching section from further down the page at open
 * time. The sections stay exactly where they are, staff-editable in the theme
 * editor, and the dialog is a second view onto the same markup.
 *
 * A trigger whose panel is not on the page hides itself, so a product template
 * without those sections shows no dead buttons. That is why the buttons render
 * with `hidden` and are revealed here rather than the other way round: if this
 * script fails to run, the page shows nothing broken.
 */
(() => {
  const PANEL_ATTR = 'data-caviar-panel';

  /** The section elsewhere on the page whose content this trigger mirrors. */
  const panelFor = (name) => document.querySelector(`[${PANEL_ATTR}="${name}"]`);

  const buildDialog = (name, panel, closeLabel) => {
    const dialog = document.createElement('dialog');
    // The panel name rides along as a modifier so each can size to its own
    // content: the profile is a column of label/bar/value rows and reads better
    // narrow, while the serving guide is a three-column table that needs width.
    dialog.className = `caviar-dialog caviar-dialog--${name}`;
    dialog.setAttribute('aria-label', name.replace('-', ' '));

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'caviar-dialog__close';
    close.setAttribute('aria-label', closeLabel);
    close.textContent = '✕';
    close.addEventListener('click', () => dialog.close());

    const body = document.createElement('div');
    body.className = 'caviar-dialog__body';

    // Clone rather than move: the section must stay on the page, and a clone
    // keeps ids out of the document twice only while the dialog is open.
    const clone = panel.cloneNode(true);
    clone.removeAttribute('id');
    clone.removeAttribute(PANEL_ATTR);
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));

    // section-wrapper writes the band's vertical rhythm as an INLINE style when
    // the section has an explicit padding setting — 48px top and bottom here.
    // An inline style outranks any stylesheet rule, so the dialog's own
    // flattening CSS cannot remove it; it has to come off the clone directly.
    // Only the padding is stripped: the same attribute carries custom
    // properties some sections rely on.
    const dropInlinePadding = (el) => {
      ['padding-block-start', 'padding-block-end', 'padding-top', 'padding-bottom']
        .forEach((prop) => el.style.removeProperty(prop));
      if (!el.getAttribute('style')) el.removeAttribute('style');
    };
    dropInlinePadding(clone);
    clone.querySelectorAll('[style]').forEach(dropInlinePadding);
    body.appendChild(clone);

    dialog.append(close, body);
    // Clicking the backdrop closes, matching the source's modal behaviour.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => dialog.remove());
    return dialog;
  };

  const wire = (root) => {
    root.querySelectorAll('[data-caviar-open]').forEach((trigger) => {
      const name = trigger.getAttribute('data-caviar-open');
      const panel = panelFor(name);

      // No section on this template — leave the trigger hidden.
      if (!panel) return;

      trigger.hidden = false;
      if (trigger.dataset.caviarWired === 'true') return;
      trigger.dataset.caviarWired = 'true';

      trigger.addEventListener('click', () => {
        const dialog = buildDialog(name, panel, trigger.textContent.trim());
        document.body.appendChild(dialog);
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          // No <dialog> support: fall back to scrolling to the real section
          // rather than trapping the shopper in a half-open overlay.
          dialog.remove();
          panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  };

  const init = () => wire(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders sections in place; re-wire when it does, since
  // a section being added or removed changes which triggers have a panel.
  document.addEventListener('shopify:section:load', () => wire(document));
  document.addEventListener('shopify:section:unload', () => wire(document));
})();
